import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Flag, Package, Bike, Phone, KeyRound, XCircle, TriangleAlert, RefreshCw } from "lucide-react";
import { rpc } from "@/lib/supabase";
import { supabase } from "@/lib/supabase";
import { Card, Loader, EstimacionBadge } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { StatusTimeline } from "@/components/StatusTimeline";
import { formatCOP, formatKm, VEHICLE_LABELS, CARGO_CATEGORY_LABELS } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function ServiceDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [offerTimeout, setOfferTimeout] = useState(null);
  const [reason, setReason] = useState("");
  const [incident, setIncident] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await rpc("get_service_detail", { p_service_id: id });
      setD(data);
    } catch (e) {
      toast.error(e?.message || "No se pudo cargar");
    }
  }, [id]);

  // Tiempo de expiración de ofertas desde la configuración real (pricing_rules), sin duplicar el valor
  useEffect(() => {
    const vt = d?.service?.vehicle_type;
    if (!vt || offerTimeout != null) return;
    supabase
      .from("pricing_rules")
      .select("offer_timeout_seconds")
      .eq("vehicle_type", vt)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.offer_timeout_seconds) setOfferTimeout(data.offer_timeout_seconds);
      });
  }, [d, offerTimeout]);

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  // Realtime: actualización instantánea del estado sin recargar (RLS: solo dueño/admin)
  useEffect(() => {
    const ch = supabase
      .channel(`service-${id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "services", filter: `id=eq.${id}` },
        () => load()
      )
      .subscribe();
    // refresco inmediato al volver a la pestaña (los timers se pausan en segundo plano)
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      supabase.removeChannel(ch);
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [id, load]);

  if (!d) return <Loader />;
  const s = d.service;
  const active = !["DELIVERED", "CANCELLED"].includes(s.status);
  const canCancel = ["REQUESTED", "SEARCHING_DRIVER", "ACCEPTED", "DRIVER_GOING_TO_PICKUP"].includes(s.status);

  const act = async (fn, params, msg) => {
    setBusy(true);
    try {
      await rpc(fn, params);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/business/services" className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-white">
        <ArrowLeft size={15} /> Volver a servicios
      </Link>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="service-detail">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-white">Envío #{s.id.slice(0, 8)}</h1>
          <div className="mt-2"><StatusBadge status={s.status} /></div>
        </div>
        <div className="text-right">
          <div className="font-heading text-2xl font-black text-[#FF6A00]">{formatCOP(s.quoted_price)}</div>
          <div className="flex items-center justify-end gap-2 text-xs text-gray-500">
            {formatKm(s.estimated_distance_km)}
            {s.is_estimated_distance ? (
              <EstimacionBadge />
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500 bg-emerald-500/15 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-emerald-400">
                Por carretera{s.estimated_duration_min ? ` · ~${s.estimated_duration_min} min` : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Driver search status */}
          {s.status === "SEARCHING_DRIVER" && (
            <Card
              className={d.has_active_offer ? "border-blue-500/40 bg-blue-500/5" : "border-amber-500/40 bg-amber-500/5"}
              data-testid="driver-search-status"
            >
              <div className="flex items-center gap-4">
                {d.has_active_offer ? (
                  <div className="h-9 w-9 shrink-0 animate-spin rounded-full border-4 border-[#27272A] border-t-[#FF6A00]" />
                ) : (
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-amber-500/15 text-amber-400">
                    <TriangleAlert size={18} />
                  </div>
                )}
                <div>
                  <div className="font-heading text-sm font-bold text-white">
                    {d.has_active_offer
                      ? "Oferta enviada a un conductor"
                      : "Sin conductores disponibles por ahora"}
                  </div>
                  <p className="text-xs text-gray-400">
                    {d.has_active_offer
                      ? `El conductor tiene ${offerTimeout ?? "…"} segundos para aceptar tu envío.`
                      : "Estamos reintentando automáticamente. También puedes usar el botón Reintentar búsqueda."}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Delivery code */}
          {active && (
            <Card className="border-[#FF6A00]/40 bg-[#FF6A00]/5" data-testid="delivery-code-card">              <div className="flex items-center gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#FF6A00]/15 text-[#FF6A00]">
                  <KeyRound size={22} />
                </div>
                <div className="grow">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Código de entrega</div>
                  <div className="font-heading text-3xl font-black tracking-[0.3em] text-white" data-testid="delivery-code-value">
                    {s.delivery_code}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-400">
                Comparte este código con quien recibe. El conductor lo necesita para marcar la entrega.
              </p>
            </Card>
          )}

          {/* Route */}
          <Card>
            <h3 className="mb-4 font-heading text-base font-bold text-white">Ruta</h3>
            <div className="space-y-1">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 text-[#FF6A00]" />
                <div>
                  <div className="text-sm font-semibold text-white">{s.pickup_address}</div>
                  <div className="text-xs text-gray-500">{s.pickup_city} · {s.pickup_reference || "Sin referencia"}</div>
                </div>
              </div>
              <div className="ml-[8px] h-5 w-0.5 bg-[#27272A]" />
              <div className="flex items-start gap-3">
                <Flag size={18} className="mt-0.5 text-emerald-400" />
                <div>
                  <div className="text-sm font-semibold text-white">{s.dropoff_address}</div>
                  <div className="text-xs text-gray-500">{s.dropoff_city} · {s.dropoff_reference || "Sin referencia"}</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Package */}
          <Card>
            <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-white">
              <Package size={17} className="text-[#FF6A00]" /> Carga
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
              <Info label="Categoría" value={CARGO_CATEGORY_LABELS[s.package_category] || s.package_type} />
              <Info label="Cantidad" value={s.package_count != null ? String(s.package_count) : "—"} />
              <Info label="Peso" value={s.package_weight_kg ? `${s.package_weight_kg} kg` : "—"} />
              <Info label="Dimensiones" value={s.package_dimensions} />
              <Info label="Vehículo" value={VEHICLE_LABELS[s.vehicle_type]} />
              <Info label="Destinatario" value={s.recipient_name} />
              <Info label="Tel. destinatario" value={s.recipient_phone} />
            </div>
            {s.package_notes && (
              <div className="mt-4 rounded-xl bg-[#0d0d0f] p-3 text-sm text-gray-300">{s.package_notes}</div>
            )}
          </Card>

          {/* Driver */}
          {d.driver?.full_name && (
            <Card>
              <h3 className="mb-4 font-heading text-base font-bold text-white">Conductor asignado</h3>
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#18181B] text-[#FF6A00]">
                  <Bike size={20} />
                </div>
                <div className="grow">
                  <div className="font-semibold text-white">{d.driver.full_name}</div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Phone size={12} /> {d.driver.phone || "Sin teléfono"}
                    {d.driver.vehicle?.plate && <span>· {d.driver.vehicle.plate}</span>}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Timeline + actions */}
        <div className="space-y-6">
          <Card>
            <h3 className="mb-4 font-heading text-base font-bold text-white">Seguimiento</h3>
            <StatusTimeline history={d.history} currentStatus={s.status} />
          </Card>

          {active && (
            <Card>
              <h3 className="mb-4 font-heading text-base font-bold text-white">Acciones</h3>
              <div className="space-y-2">
                {s.status === "SEARCHING_DRIVER" && (
                  <Button
                    onClick={() => act("retry_dispatch", { p_service_id: s.id }, "Reintentando búsqueda")}
                    disabled={busy}
                    className="w-full justify-start gap-2 bg-[#18181B] text-white hover:bg-[#27272A]"
                    data-testid="retry-dispatch-button"
                  >
                    <RefreshCw size={16} /> Reintentar búsqueda
                  </Button>
                )}

                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="w-full justify-start gap-2 bg-[#18181B] text-amber-300 hover:bg-[#27272A]" data-testid="report-incident-button">
                      <TriangleAlert size={16} /> Reportar novedad
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="border-[#27272A] bg-[#141416] text-white">
                    <DialogHeader><DialogTitle>Reportar novedad</DialogTitle><DialogDescription>Describe la novedad ocurrida durante el envío.</DialogDescription></DialogHeader>
                    <Textarea value={incident} onChange={(e) => setIncident(e.target.value)} placeholder="Describe lo ocurrido…" className="border-[#27272A] bg-[#0d0d0f]" data-testid="incident-textarea" />
                    <DialogFooter>
                      <Button
                        onClick={() => incident && act("report_incident", { p_service_id: s.id, p_description: incident }, "Novedad reportada").then(() => setIncident(""))}
                        className="bg-[#FF6A00] text-black hover:bg-[#e05d00]"
                        data-testid="incident-submit"
                      >
                        Enviar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {canCancel && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="w-full justify-start gap-2 bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid="cancel-service-button">
                        <XCircle size={16} /> Cancelar envío
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="border-[#27272A] bg-[#141416] text-white">
                      <DialogHeader><DialogTitle>Cancelar envío</DialogTitle><DialogDescription>Indica el motivo de la cancelación del envío.</DialogDescription></DialogHeader>
                      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo de la cancelación…" className="border-[#27272A] bg-[#0d0d0f]" data-testid="cancel-reason-textarea" />
                      <DialogFooter>
                        <Button
                          onClick={() => act("cancel_service", { p_service_id: s.id, p_reason: reason || "Cancelado por el negocio" }, "Envío cancelado")}
                          className="bg-red-500 text-white hover:bg-red-600"
                          data-testid="cancel-confirm"
                        >
                          Confirmar cancelación
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-medium text-white">{value || "—"}</div>
  </div>
);
